<template>
    <Transition name="thread-panel">
        <aside
            v-if="drawerOpen"
            class="thread-panel"
            :class="{
                'thread-panel--banner': bannerVisible,
                'thread-panel--accent': accentStripVisible,
            }"
            data-testid="thread-panel"
        >
            <div class="thread-panel-header">
                <!-- Detail: the thread's back button, title + status, and the
                     Accept/Reject actions all live in the header now (moved out
                     of the body). -->
                <template v-if="view === 'detail' && activeThread">
                    <Button
                        icon="pi pi-arrow-left"
                        text
                        rounded
                        size="small"
                        aria-label="Back to threads list"
                        data-testid="thread-back"
                        @click="backToList"
                    />
                    <div class="thread-panel-title-group">
                        <span class="thread-panel-title">{{
                            activeThread.title
                        }}</span>
                        <Tag
                            :value="activeThread.status"
                            :severity="statusSeverity(activeThread.status)"
                        />
                    </div>
                    <div
                        v-if="activeThread.status === 'open'"
                        class="thread-panel-header-actions"
                    >
                        <label
                            class="auto-toggle"
                            v-tooltip.bottom="
                                'Apply changes to the draft as they are proposed, without asking. Accepting the thread is still what puts them into the business.'
                            "
                        >
                            <ToggleSwitch
                                :modelValue="activeThread.auto_apply"
                                data-testid="drawer-auto-apply"
                                @update:modelValue="setAutoApply"
                            />
                            <span>Auto</span>
                        </label>
                        <Button
                            label="Accept"
                            icon="pi pi-check"
                            severity="success"
                            outlined
                            size="small"
                            :disabled="
                                hasPendingConflicts || !!activeCatchUpFailure
                            "
                            :title="
                                activeCatchUpFailure
                                    ? 'Couldn\'t compare this thread with main — accepting is disabled until a comparison succeeds.'
                                    : hasPendingConflicts
                                      ? 'Behind main — resolve the conflicts before accepting.'
                                      : undefined
                            "
                            data-testid="drawer-accept"
                            @click="startResolve('accept')"
                        />
                        <Button
                            label="Reject"
                            icon="pi pi-times"
                            severity="danger"
                            outlined
                            size="small"
                            @click="startResolve('reject')"
                        />
                    </div>
                </template>

                <!-- List / create / resolve: a plain title, plus the "New
                     Thread" affordance while browsing the list (matches the
                     app's other in-context "add" header buttons). -->
                <template v-else>
                    <span class="thread-panel-title">{{ drawerHeader }}</span>
                    <Button
                        v-if="view === 'list'"
                        icon="pi pi-plus"
                        label="New Thread"
                        severity="secondary"
                        size="small"
                        :loading="isLoading"
                        data-testid="thread-new"
                        @click="startCreate"
                    />
                </template>
            </div>

            <div class="thread-panel-body">
                <!-- Inline create form -->
                <template v-if="view === 'create'">
                    <div class="drawer-form">
                        <div class="field">
                            <label for="drawer-thread-title">Title</label>
                            <InputText
                                id="drawer-thread-title"
                                v-model="createTitle"
                                placeholder="What do you want to discuss?"
                                class="w-full"
                                @keydown.enter="createNewThread"
                            />
                        </div>
                        <div class="field">
                            <label for="drawer-thread-desc"
                                >Description (optional)</label
                            >
                            <Textarea
                                id="drawer-thread-desc"
                                v-model="createDescription"
                                placeholder="Provide more context..."
                                rows="3"
                                class="w-full"
                            />
                        </div>
                        <div class="drawer-form-actions">
                            <Button
                                label="Cancel"
                                text
                                size="small"
                                @click="view = 'list'"
                            />
                            <Button
                                label="Create"
                                icon="pi pi-plus"
                                size="small"
                                :loading="isCreating"
                                :disabled="!createTitle.trim()"
                                @click="createNewThread"
                            />
                        </div>
                    </div>
                </template>

                <!-- Inline resolve form -->
                <template v-else-if="view === 'resolve'">
                    <div class="drawer-form">
                        <p>
                            {{
                                resolveAction === "accept"
                                    ? "Accepting this thread will merge all staged changes to main."
                                    : "Rejecting this thread will discard all staged changes. The discussion history is preserved."
                            }}
                        </p>
                        <!-- Accept: a "what and why" decision record. Reject: a
                             single reason (nothing is merged). -->
                        <template v-if="resolveAction === 'accept'">
                            <div class="field">
                                <label for="resolve-what">What changed</label>
                                <Textarea
                                    id="resolve-what"
                                    v-model="resolveWhat"
                                    placeholder="What did this thread change? (one line)"
                                    rows="2"
                                    class="w-full"
                                />
                            </div>
                            <div class="field">
                                <label for="resolve-why">Why</label>
                                <Textarea
                                    id="resolve-why"
                                    v-model="resolveWhy"
                                    placeholder="Why was it done / what was decided? (optional)"
                                    rows="2"
                                    class="w-full"
                                />
                                <Button
                                    label="Suggest message"
                                    icon="pi pi-sparkles"
                                    text
                                    size="small"
                                    class="suggest-btn"
                                    :loading="isSuggestingMessage"
                                    data-testid="drawer-suggest-message"
                                    @click="suggestResolveMessage"
                                />
                            </div>
                        </template>
                        <div v-else class="field">
                            <label for="resolve-reason">Reason</label>
                            <Textarea
                                id="resolve-reason"
                                v-model="resolveReason"
                                placeholder="Why are you rejecting this thread?"
                                rows="3"
                                class="w-full"
                            />
                        </div>
                        <div class="drawer-form-actions">
                            <Button
                                label="Cancel"
                                text
                                size="small"
                                @click="view = 'detail'"
                            />
                            <Button
                                :label="
                                    resolveAction === 'accept'
                                        ? 'Accept'
                                        : 'Reject'
                                "
                                :icon="
                                    resolveAction === 'accept'
                                        ? 'pi pi-check'
                                        : 'pi pi-times'
                                "
                                :severity="
                                    resolveAction === 'accept'
                                        ? 'success'
                                        : 'danger'
                                "
                                size="small"
                                :loading="isResolving"
                                :disabled="!canResolveDrawer"
                                @click="resolveCurrentThread"
                            />
                        </div>
                    </div>
                </template>

                <template v-else-if="view === 'detail' && activeThread">
                    <Button
                        icon="pi pi-window-maximize"
                        label="Open full page"
                        text
                        size="small"
                        class="drawer-maximize"
                        data-testid="drawer-maximize"
                        @click="openFullPage"
                    />
                    <ThreadConversation
                        v-if="drawerThreadId"
                        :thread-id="drawerThreadId"
                    />
                </template>

                <!-- Loading state for thread detail -->
                <template v-else-if="view === 'detail' && isFetching">
                    <div class="drawer-loading">
                        <ProgressSpinner />
                    </div>
                </template>

                <!-- Threads list view — the "New Thread" button now lives in the
                     panel header (above). -->
                <template v-else>
                    <ProgressSpinner v-if="isFetching" />

                    <div v-else-if="threads.length === 0" class="drawer-empty">
                        <i
                            class="pi pi-comments"
                            style="font-size: 2rem; opacity: 0.3"
                        />
                        <p>No threads yet.</p>
                        <p class="muted">
                            Start one to discuss ideas and enact changes.
                        </p>
                    </div>

                    <div v-else class="drawer-threads-list">
                        <template
                            v-for="section in threadSections"
                            :key="section.key"
                        >
                            <div
                                v-if="section.label"
                                class="drawer-section-header"
                                :class="{
                                    'is-secondary': section.key === 'other',
                                }"
                                :data-testid="`thread-section-${section.key}`"
                            >
                                {{ section.label }}
                            </div>
                            <div
                                v-for="thread in section.threads"
                                :key="thread.identity"
                                class="drawer-thread-card"
                                :class="{
                                    'is-viewing':
                                        thread.identity ===
                                        releaseStore.activeThreadId,
                                    'is-secondary': section.key === 'other',
                                }"
                                data-testid="thread-card"
                                @click="openThread(thread.identity)"
                            >
                                <div class="drawer-card-top">
                                    <span class="drawer-card-title">{{
                                        thread.title
                                    }}</span>
                                    <span class="drawer-card-top-right">
                                        <span
                                            v-if="
                                                thread.identity ===
                                                releaseStore.activeThreadId
                                            "
                                            class="viewing-hint"
                                            title="You are viewing this draft"
                                        >
                                            <i class="pi pi-eye" />
                                            Viewing
                                        </span>
                                        <Tag
                                            :value="thread.status"
                                            :severity="
                                                statusSeverity(thread.status)
                                            "
                                        />
                                    </span>
                                </div>
                                <div class="drawer-card-meta">
                                    <span v-if="thread.created_by">
                                        {{ thread.created_by.name }}
                                    </span>
                                    <span v-if="thread.created_at">
                                        &middot;
                                        {{ formatDate(thread.created_at) }}
                                    </span>
                                    <span
                                        >&middot;
                                        {{ thread.entry_count }} entries</span
                                    >
                                </div>
                                <div
                                    v-if="thread.description"
                                    class="drawer-card-desc"
                                >
                                    {{ thread.description }}
                                </div>
                            </div>
                        </template>
                    </div>
                </template>
            </div>
        </aside>
    </Transition>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import ProgressSpinner from "primevue/progressspinner";
import Tag from "primevue/tag";
import Textarea from "primevue/textarea";
import ToggleSwitch from "primevue/toggleswitch";
import { computed, onUnmounted, ref, watch } from "vue";
import { useRouter } from "vue-router";

import ThreadConversation from "@/components/Thread/ThreadConversation.vue";
import { groupThreadsByPage } from "@/components/Thread/thread-sections";
import { useRightPanelOffset } from "@/composables/right-panel-offset";
import { onEntityChange } from "@/stores/cache-invalidation";
import { useCurrentEntityManager } from "@/stores/entity-store";
import { useReleaseStore } from "@/stores/release-store";
import { useThreadsStore } from "@/stores/threads-store";

const store = useThreadsStore();

const setAutoApply = (enabled: boolean) => {
    void store.setAutoApply(enabled);
};

// The last catch-up failure for the open thread: with the comparison failed,
// zero recorded conflicts prove nothing — accepting must stay blocked.
const activeCatchUpFailure = computed(() =>
    store.activeThread
        ? store.catchUpFailures?.[store.activeThread.identity]
        : undefined
);
const releaseStore = useReleaseStore();
const router = useRouter();

// The entity shown on the current page — used to split the list into threads
// "On this page" vs "On other pages".
const { identity: currentEntityIdentity } = useCurrentEntityManager();

const {
    threads,
    activeThread,
    hasPendingConflicts,
    isLoading,
    isFetching,
    drawerOpen,
    drawerThreadId,
} = storeToRefs(store);

// Internal view state: 'list' | 'detail' | 'create' | 'resolve'
const view = ref<"list" | "detail" | "create" | "resolve">("list");

// Identities of threads that affect the current page's entity (open threads, via
// the store's entity → threads map). Resolved threads carry no affected entities,
// so they always fall into "On other pages".
const onThisPageIds = computed(() => {
    const id = currentEntityIdentity.value;
    if (!id || id === "unknown") return new Set<string>();
    return new Set(store.getThreadsForEntity(id).map((t) => t.identity));
});

// Group the list into "On this page" / "On other pages".
const threadSections = computed(() =>
    groupThreadsByPage(threads.value, onThisPageIds.value)
);

// Create form state
const createTitle = ref("");
const createDescription = ref("");
const isCreating = ref(false);

// Resolve form state. Accept uses what + why (the decision record); reject
// uses a single reason.
const resolveAction = ref<"accept" | "reject">("accept");
const resolveWhat = ref("");
const resolveWhy = ref("");
const resolveReason = ref("");
const isResolving = ref(false);
const isSuggestingMessage = ref(false);

// The field that must be filled before resolving: "what" on accept, "reason"
// on reject. "why" is always optional.
const canResolveDrawer = computed(() =>
    resolveAction.value === "accept"
        ? !!resolveWhat.value.trim()
        : !!resolveReason.value.trim()
);

// Pre-fill the (editable) accept fields with an LLM suggestion (split into
// what/why) built from the thread's discussion + changes. Never blocks
// accepting: the store toasts and returns null on failure, leaving manual
// entry intact.
const suggestResolveMessage = async () => {
    if (!drawerThreadId.value) return;
    isSuggestingMessage.value = true;
    try {
        const suggestion = await store.generateAcceptMessage(
            drawerThreadId.value
        );
        if (suggestion) {
            resolveWhat.value = suggestion.what;
            resolveWhy.value = suggestion.why;
        }
    } finally {
        isSuggestingMessage.value = false;
    }
};

// The release banner sits directly under the top bar (top: --menu-bar-height,
// height: --release-banner-height) whenever a release is being viewed, and on a
// thread branch the top bar shows the 3px `.thread-accent-strip` instead. Either
// one would paint over this panel's header, so the panel's top offset grows to
// clear whichever is showing (via the `--banner` / `--accent` classes below).
//
// Sourced from the shared composable so a second right-side panel can't be
// written without these offsets — which is exactly what happened to
// DomainObjectDrawer.
const { bannerVisible, accentStripVisible } = useRightPanelOffset();

const drawerHeader = computed(() => {
    switch (view.value) {
        case "create":
            return "New Thread";
        case "resolve":
            return resolveAction.value === "accept"
                ? "Accept Thread"
                : "Reject Thread";
        case "detail":
            return "";
        default:
            return "Threads";
    }
});

// Sync view state with drawerThreadId and activate thread branch
watch(
    drawerThreadId,
    async (threadId) => {
        if (threadId) {
            view.value = "detail";
            await store.fetchThread(threadId);
            // Track the open thread in the URL (?thread=) so it survives reloads.
            if (activeThread.value?.branch) {
                releaseStore.setOpenThreadId(threadId);
                if (router.currentRoute.value.query.thread !== threadId) {
                    router.push({
                        query: {
                            ...router.currentRoute.value.query,
                            thread: threadId,
                        },
                    });
                }
            }
        } else {
            view.value = "list";
        }
    },
    { immediate: true }
);

// Fetch threads when drawer opens, reset view when it closes
watch(drawerOpen, async (open) => {
    if (open) {
        await store.fetchThreads();
    } else {
        view.value = drawerThreadId.value ? "detail" : "list";
    }
});

// Keep the drawer open while a thread branch is being viewed. The close
// affordances are already disabled on a branch (dismissable / escape / close
// icon above), but this also re-opens it should anything else flip drawerOpen
// off — the drawer is the only "back to working copy" affordance for the branch,
// so it must stay visible until the branch is exited. immediate:true also covers
// deep-link / reload arrivals at a ?thread= URL.
watch(
    () => releaseStore.viewingThreadBranch,
    (onBranch) => {
        if (onBranch) drawerOpen.value = true;
    },
    { immediate: true }
);

// Open an SSE connection for the active thread so all viewers see contributions and
// agent activity in real time (replaces background polling).
watch(
    [drawerOpen, drawerThreadId],
    ([open, threadId]) => {
        if (open && threadId) {
            void store.connectToThread(threadId);
            // Open/view catch-up trigger: bring the branch up to date with main on
            // open so the viewer sees fresh content + diff badges and any behind-main
            // conflicts surface. Best-effort; refetches + re-syncs once caught up.
            void store.catchUpThread(threadId);
        } else {
            store.disconnectThread();
        }
    },
    { immediate: true }
);

// Surface the non-agent (user) branch edits in the detail view so a reviewer
// sees what they changed directly alongside the agent's work. Fetched when a
// thread is opened and refreshed whenever an entity changes — so the user's own
// direct edit (a silent branch write that creates no thread entry) shows up
// without needing to reopen the thread.
const refreshUserChanges = () => {
    if (drawerThreadId.value) void store.fetchUserChanges(drawerThreadId.value);
};
watch(drawerThreadId, refreshUserChanges, { immediate: true });
const stopUserChangeRefresh = onEntityChange(
    () => releaseStore.viewingThreadBranch,
    refreshUserChanges
);
onUnmounted(() => stopUserChangeRefresh());

onUnmounted(() => {
    store.disconnectThread();
});

const openThread = (threadId: string) => {
    store.selectThread(threadId);
};

const backToList = () => {
    store.selectThread(undefined);
    // Clear the open-thread id and drop the ?thread= query param.
    if (releaseStore.activeThreadId) {
        releaseStore.clearOpenThreadId();
        const { thread: _, ...remainingQuery } =
            router.currentRoute.value.query;
        router.replace({ query: remainingQuery });
    }
};

const startCreate = () => {
    createTitle.value = "";
    createDescription.value = "";
    view.value = "create";
};

const createNewThread = async () => {
    if (!createTitle.value.trim()) return;
    isCreating.value = true;
    const data = await store.createThread(
        createTitle.value.trim(),
        createDescription.value.trim() || undefined
    );
    isCreating.value = false;
    if (data) {
        store.selectThread(data.identity);
    }
};

const startResolve = (action: "accept" | "reject") => {
    resolveAction.value = action;
    resolveWhat.value = "";
    resolveWhy.value = "";
    resolveReason.value = "";
    view.value = "resolve";
};

const resolveCurrentThread = async () => {
    if (!canResolveDrawer.value || !drawerThreadId.value) return;
    isResolving.value = true;
    // Accept: "what" is the primary reason, "why" the rationale trailer.
    // Reject: the single reason carries the whole explanation.
    await store.resolveThread(
        drawerThreadId.value,
        resolveAction.value,
        resolveAction.value === "accept"
            ? resolveWhat.value.trim()
            : resolveReason.value.trim(),
        resolveAction.value === "accept" ? resolveWhy.value.trim() : undefined
    );
    isResolving.value = false;
    // Always drop back to the detail view — never close/navigate away. On a
    // "conflict" outcome the store has refetched the thread with the pick-a-side
    // records, so detail now renders the "Resolve N conflicts" CTA and the Accept
    // button stays disabled until each conflict is resolved (hasPendingConflicts).
    // A clean "resolved" flips the status (Accept/Reject hide); an "error" leaves
    // it open with no conflicts so Accept stays available.
    view.value = "detail";
};

// Route to the dedicated Reconcile page for this thread. The route is nested
// under the current org/stek path (`/{orgName}/steks/{stekName}/reconcile`);
// pushing by name without explicit params relies on vue-router's named-route
// resolution reusing the current route's params (see e.g. StekSettings.vue's
// `router.push({ name: "integrationManagement" })`) — safe here because the
// drawer only renders once a stek is selected (ApplicationLayout.vue).
const openFullPage = () => {
    if (!drawerThreadId.value) return;
    const threadId = drawerThreadId.value;
    store.closeDrawer();
    router.push({ name: "thread", params: { thread: threadId } });
};

const statusSeverity = (status: string) => {
    switch (status) {
        case "accepted":
            return "success";
        case "rejected":
            return "danger";
        default:
            return "info";
    }
};

const formatDate = (date: string | Date) => {
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
    });
};
</script>

<style scoped>
/* In-layout right panel — a real column in `.page-with-sidebar` (see
   ApplicationLayout.vue) rather than a floating overlay, so opening it reflows
   the page content narrower instead of covering it. Mirrors the lefthand
   `#sidebar` and the righthand `.history-sidebar`: a flex column starting at the
   viewport top, so it needs the same `padding-top: var(--menu-bar-height)` to
   clear the fixed bar (otherwise its header hides under it). Its own border,
   tucked under the release banner when one is shown. */
.thread-panel {
    flex: 0 0 420px;
    width: 420px;
    height: 100vh;
    padding-top: var(--menu-bar-height);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: var(--p-content-background);
    border-left: 1px solid var(--surface-border, #dee2e6);
}

/* PrimeVue's surface scale is intentionally overridden here because thread
   content uses these tokens for bubbles, cards, code blocks, and the composer.
   Keeping the override scoped avoids changing unrelated screens while making
   every thread surface follow the application's dark-mode palette. */
:global(html.dark-mode) .thread-panel {
    --p-content-background: #222222;
    --p-surface-0: #222222;
    --p-surface-50: #292929;
    --p-surface-100: #333333;
    --p-surface-200: #4a4a4a;
    --p-content-border-color: #5a5a5a;
    --surface-border: #5a5a5a;
    --p-text-muted-color: #c2c2c2;
    --p-text-secondary-color: #dddddd;
}

:global(html.dark-mode) .thread-panel .agent-bubble {
    --p-primary-50: #382255;
    --p-primary-100: #633a91;
}

.thread-panel--banner {
    padding-top: calc(var(--menu-bar-height) + var(--release-banner-height));
}

/* On a thread branch, clear the 3px `.thread-accent-strip` pinned under the top
   bar (see ApplicationLayout) so it doesn't paint across the header. */
.thread-panel--accent {
    padding-top: calc(
        var(--menu-bar-height) + var(--thread-accent-strip-height)
    );
}

.thread-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.5rem;
    min-height: 3rem;
    padding: 0.5rem 0.75rem 0.5rem 1rem;
    border-bottom: 1px solid var(--p-surface-200);
}

.thread-panel-title {
    font-weight: 600;
    font-size: 1.05rem;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* The panel is a fixed 420px and the actions never shrink, so the title gets
   its own row. */
.thread-panel-title-group {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
    min-width: 0;
}

.thread-panel-title-group .p-tag {
    flex-shrink: 0;
}

.auto-toggle {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin-right: 0.35rem;
    font-size: 0.8rem;
    color: var(--text-color-secondary);
    cursor: pointer;
}

.thread-panel-header-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.4rem;
    flex: 0 0 100%;
}

.thread-panel-body {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding: 1.25rem;
}

/* Open/close is a subtle fade + slide; the column's width appears/disappears
   with v-if (reflowing the page), while its content eases in. */
.thread-panel-enter-active,
.thread-panel-leave-active {
    transition:
        opacity 160ms ease,
        transform 160ms ease;
}

.thread-panel-enter-from,
.thread-panel-leave-to {
    opacity: 0;
    transform: translateX(16px);
}

.drawer-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.field label {
    font-weight: 500;
    font-size: 0.9rem;
}

.drawer-form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
}

.drawer-maximize {
    align-self: flex-start;
    margin-bottom: 0.25rem;
}

.drawer-loading {
    display: flex;
    justify-content: center;
    padding: 3rem 0;
}

.drawer-empty {
    text-align: center;
    padding: 3rem 1rem;
    color: var(--p-text-muted-color);
}

.drawer-empty p {
    margin: 0.25rem 0;
}

.drawer-empty .muted {
    font-size: 0.85rem;
    opacity: 0.7;
}

.drawer-threads-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
}

.drawer-section-header {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--p-text-muted-color);
    padding: 0.25rem 0.1rem;
    margin-top: 0.25rem;
}

/* No top margin for the very first section header so it sits flush at the top of
   the list. */
.drawer-threads-list > .drawer-section-header:first-child {
    margin-top: 0;
}

/* De-emphasise the "On other pages" group so focus lands on the threads that
   relate to the current page. Kept subtle (not a "disabled" grey): the cards
   stay full-colour, just dialled back, and hovering restores full prominence so
   they still read as interactive. The section label carries the meaning, so this
   is a reinforcement, not the sole cue. */
.drawer-section-header.is-secondary {
    opacity: 0.65;
}

.drawer-thread-card.is-secondary {
    opacity: 0.6;
    transition:
        opacity 0.15s,
        border-color 0.15s;
}

.drawer-thread-card.is-secondary:hover {
    opacity: 1;
}

/* The branch currently being viewed always stays at full prominence, even if it
   happens to fall under "On other pages". */
.drawer-thread-card.is-secondary.is-viewing {
    opacity: 1;
}

.drawer-thread-card {
    border: 1px solid var(--p-surface-200);
    border-radius: 8px;
    padding: 0.75rem;
    cursor: pointer;
    transition: border-color 0.15s;
}

.drawer-thread-card:hover {
    border-color: var(--p-primary-color);
}

/* The thread whose branch is currently being viewed (?thread=): a subtle accent
   so it stands out in the list without shouting. */
.drawer-thread-card.is-viewing {
    border-color: var(--p-primary-color);
    border-left-width: 3px;
    background: color-mix(in srgb, var(--p-primary-color) 6%, transparent);
}

.drawer-card-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.25rem;
}

.drawer-card-top-right {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    flex-shrink: 0;
}

.viewing-hint {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.7rem;
    font-weight: 500;
    color: var(--p-primary-color);
    white-space: nowrap;
}

.viewing-hint .pi {
    font-size: 0.7rem;
}

.drawer-card-title {
    font-weight: 600;
    font-size: 0.95rem;
}

.drawer-card-meta {
    font-size: 0.8rem;
    color: var(--p-text-muted-color);
}

.drawer-card-desc {
    margin-top: 0.35rem;
    font-size: 0.85rem;
    color: var(--p-text-secondary-color);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>
